/**
 * ORDEM ALFABÉTICA — a configuração ÚNICA de como listas de nomes aparecem.
 *
 * Toda lista de PESSOAS ou de CADASTROS identificados por nome (equipe,
 * freelancers, responsáveis, obras, veículos, equipamentos, setores...) passa
 * por aqui, e sai em ordem CRESCENTE (A → Z) — o mesmo padrão que a Agenda já
 * usava para as linhas da grade.
 *
 * Por que na TELA, e não só no `ORDER BY` do servidor: é na tela que a ordem foi
 * pedida, e ela não pode depender de qual rota montou a lista nem de quem chamou
 * a API antes. O servidor pode (e em vários pontos já vai) mandar ordenado; isto
 * aqui é a garantia.
 *
 * O que NÃO passa por aqui, de propósito: listas cuja ordem natural é DATA ou
 * NÚMERO (atividades, checkpoints, solicitações, histórico) e rankings com
 * significado (a equipe sugerida pela IA, que vem por adequação ao serviço).
 */

/**
 * Compara dois nomes do jeito que se espera de uma lista em português:
 *  - `pt-BR` + `sensitivity: "base"`: acento e maiúscula não mudam a posição
 *    ("Álvaro" fica junto de "Adolfo", não no fim; "ana" junto de "Ana");
 *  - `numeric`: número dentro do nome segue a ordem numérica ("Equipe 2" antes
 *    de "Equipe 10"), e não a do texto.
 */
export const compararNomes = (a: unknown, b: unknown): number =>
  String(a ?? "")
    .trim()
    .localeCompare(String(b ?? "").trim(), "pt-BR", { sensitivity: "base", numeric: true });

/**
 * Devolve uma CÓPIA da lista em ordem alfabética pelo nome que `nome` extrai de
 * cada item. Nunca mexe no array original — há telas que usam a lista de
 * referência por índice ou por id, e ordenar no lugar mudaria o que elas veem.
 *
 * Itens sem nome (vazio) vão para o FIM: um cadastro incompleto no topo da
 * lista empurraria os nomes de verdade para baixo. Empates mantêm a ordem em
 * que chegaram (o `sort` do JavaScript é estável).
 */
export function ordenarPorNome<T>(lista: readonly T[], nome: (item: T) => unknown): T[] {
  const semNome = (item: T) => String(nome(item) ?? "").trim() === "";
  return [...lista].sort((a, b) => {
    const va = semNome(a);
    const vb = semNome(b);
    if (va !== vb) return va ? 1 : -1;
    return compararNomes(nome(a), nome(b));
  });
}
